import { Reset } from '@hendrx/icons/Reset';
import { Button, Tooltip } from '@mantine/core';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import { useTranslation } from 'react-i18next';

import { ActivityThemeStyles } from 'shared/common/themes/theme';
import { getRootMap } from 'shared/common/utils/yjs';
import { fonts } from 'shared/fonts';
import { defaultActivityThemeStyles } from 'shared/themes/defaultActivityThemeStyles';
import { getThemeDefaultObjectValues, getThemeDefaultValue } from 'shared/utils/ActivityThemeStylesUtils';
import { useParentTheme } from 'shared/utils/useParentTheme';
import { useObservedProperty } from 'shared/utils/yjs.client';

import classes from './ClearActivityStyleProperty.module.scss';
import { useCurrentDocument } from '../../yjs/YjsProvider';

type ClearActivityStylePropertyProps = {
  path: string;
  globalPath?: string;
  partialGlobalPath?: string;
  pathsBatch?: string[];
};

export function ClearActivityStyleProperty(props: ClearActivityStylePropertyProps) {
  const { path, globalPath, partialGlobalPath, pathsBatch } = props;

  const { t } = useTranslation('components.theme');

  const document = useCurrentDocument();
  const rootMap = getRootMap(document);

  const [activityThemeStyles, setActivityThemeStyles] = useObservedProperty<Partial<ActivityThemeStyles>>(
    rootMap,
    'activityThemeStyles'
  );
  const activityThemeValue = get(activityThemeStyles, path);
  const { data: parentTheme } = useParentTheme();

  const themeDefaultValue = getThemeDefaultValue(
    activityThemeStyles,
    parentTheme?.styles,
    defaultActivityThemeStyles,
    path,
    globalPath
  );

  const themeDefaultObjectValues = getThemeDefaultObjectValues(
    themeDefaultValue,
    activityThemeStyles,
    parentTheme?.styles,
    defaultActivityThemeStyles,
    partialGlobalPath
  );

  const getThemeLabel = (path: string) => {
    const parentThemeLabel = path.includes('fontFamily')
      ? fonts.find(font => font.value === themeDefaultValue)?.label
      : globalPath?.includes('fontFamily')
      ? fonts.find(font => font.value === themeDefaultValue)?.label
      : themeDefaultValue;
    return parentThemeLabel;
  };

  const parentThemeLabel = typeof themeDefaultValue === 'object' ? themeDefaultObjectValues : getThemeLabel(path);

  const clearStyleProperty = () => {
    setActivityThemeStyles(prevTheme => omit(structuredClone(prevTheme), pathsBatch ? pathsBatch : path));
  };

  return isNil(activityThemeValue) || isEqual(activityThemeValue, themeDefaultValue) ? null : (
    <Tooltip
      position="bottom"
      label={t('parentThemeDefaultValue', {
        value: `${parentThemeLabel || '-'}` // Make sure this is always a string, otherwise next-intl throws an error
      })}
    >
      <Button className={classes.clearButton} size="compact-md" onClick={clearStyleProperty}>
        <Reset className={classes.clearIcon} />
      </Button>
    </Tooltip>
  );
}
